/**
 * Estimation de la durée d'un cycle du showcase player (apps/marketing).
 *
 * Le worker (apps/video-worker) capture EXACTEMENT un cycle du player, dont la
 * durée est déterministe car pilotée par des timers fixes. On la calcule à
 * partir du nombre de questions jouées. Sert aussi à afficher la « durée
 * estimée » dans l'éditeur de vidéo (apps/app).
 *
 * ⚠️ Les constantes ci-dessous sont le MIROIR de `TIMING` dans
 * apps/marketing/src/components/showcase/showcase-player.tsx. Toute modif des
 * timers du player doit être répercutée ici (et inversement).
 */

export const SHOWCASE_TIMING = {
  intro: 2500,
  reading: 1500,
  selecting: 700,
  revealed: 2200,
  final: 2600,
} as const;

/**
 * Timing du mode REEL (capture Instagram), joué à vitesse NATURELLE (pas
 * d'étirement) — la durée du Reel suit le nombre de questions. Phases longues
 * pour la COMPRÉHENSION :
 *  - `reading` (~4 s) : lire la QUESTION (les choix sont encore masqués) ;
 *  - `selecting` (~5 s) : les CHOIX sont affichés → temps de les lire avant la
 *    révélation de la bonne réponse.
 */
export const SHOWCASE_TIMING_REEL = {
  intro: 2500,
  reading: 4000,
  selecting: 5000,
  revealed: 2500,
  // `final` court : dès la dernière réponse, l'écran de score natif n'est tenu
  // qu'un instant (le temps de l'animer/capturer) → la cover de clôture (avec les
  // textes) prend le relais TOUT DE SUITE. Pas de temps mort en fin de Reel.
  final: 1000,
} as const;

export type ShowcaseTiming = {
  intro: number;
  reading: number;
  selecting: number;
  revealed: number;
  final: number;
};

/**
 * Exercice « sur la feuille » (`paper`) : après la sélection, la touche choisie
 * reste allumée puis « Check » s'enfonce avant la révélation. Phase AJOUTÉE au
 * mode paper seulement (le mode historique ne change pas). Miroir du player
 * (`showcase-v2-player.tsx`, `showcase-player.tsx`, phase `checking`).
 */
export const SHOWCASE_PAPER_CHECK_MS = 550;

export interface ShowcaseDurationOptions {
  /** rendu « sur la feuille » : + `SHOWCASE_PAPER_CHECK_MS` par question */
  paper?: boolean;
}

/** Durée d'une seule question (lecture → sélection → (Check) → révélation). */
export function showcaseStepDurationMs(
  timing: ShowcaseTiming = SHOWCASE_TIMING,
  opts: ShowcaseDurationOptions = {},
): number {
  return (
    timing.reading + timing.selecting + (opts.paper ? SHOWCASE_PAPER_CHECK_MS : 0) + timing.revealed
  );
}

/**
 * Feuille de brouillon rejouée (player V2) : entre la lecture de la question et
 * l'apparition des choix, les lignes réellement écrites par l'élève
 * (`attempt_scratch_lines`) s'écrivent une à une. Même vitesse en Reel (lisible).
 */
export const SHOWCASE_SCRATCH_TIMING = {
  /** la feuille s'ouvre */
  open: 900,
  /** une ligne qui s'écrit d'un coup (ancien rejeu, sans bouts connus), puis le temps de la lire */
  line: 1300,
  /** un appui sur un bout de la palette */
  tap: 320,
  /** ligne complète : l'élève la valide, ses HORA s'affichent */
  check: 900,
  /** la feuille complète reste à lire avant les choix */
  hold: 1200,
} as const;

/**
 * Durée d'UNE ligne rejouée : un appui par bout (`atomCount`), puis la
 * validation. Sans bouts connus (0) : la ligne s'écrit d'un coup (`line`).
 */
export function showcaseScratchLineDurationMs(atomCount: number): number {
  const n = Math.max(0, Math.floor(atomCount));
  const t = SHOWCASE_SCRATCH_TIMING;
  return n === 0 ? t.line : n * t.tap + t.check;
}

/**
 * Durée de la phase brouillon d'une question (0 si l'élève n'a rien écrit).
 * `lines` = nombre de bouts de chaque ligne (rejeu appui par appui), ou un
 * simple nombre de lignes (rejeu ligne par ligne, durée `line` chacune).
 */
export function showcaseScratchDurationMs(lines: number | readonly number[]): number {
  const atoms =
    typeof lines === 'number'
      ? Array.from({ length: Math.max(0, Math.floor(lines)) }, () => 0)
      : lines;
  if (atoms.length === 0) return 0;
  const t = SHOWCASE_SCRATCH_TIMING;
  return t.open + atoms.reduce((sum, n) => sum + showcaseScratchLineDurationMs(n), 0) + t.hold;
}

/**
 * Durée totale (ms) d'un cycle du showcase pour `stepCount` questions jouées :
 * intro + Σ(étapes) + bilan final. `stepCount` est borné à >= 0.
 * `scratchLineCounts` : pour chaque question qui a un brouillon, le nombre de
 * bouts de chacune de ses lignes (ou son nombre de lignes, ancien rejeu).
 */
export function estimateShowcaseDurationMs(
  stepCount: number,
  timing: ShowcaseTiming = SHOWCASE_TIMING,
  scratchLineCounts: ReadonlyArray<number | readonly number[]> = [],
  opts: ShowcaseDurationOptions = {},
): number {
  const steps = Math.max(0, Math.floor(stepCount));
  const scratch = scratchLineCounts.reduce<number>(
    (sum, n) => sum + showcaseScratchDurationMs(n),
    0,
  );
  return timing.intro + steps * showcaseStepDurationMs(timing, opts) + scratch + timing.final;
}

/**
 * Plafond par défaut (ms) de la fenêtre de capture réelle. La capture Playwright
 * filme l'interface EN TEMPS RÉEL : on ne peut pas filmer 1h/2h/8h. Au-delà du
 * plafond, on capture une lecture lente d'au plus `DEFAULT_MAX_CAPTURE_MS` puis
 * la composition FFmpeg la boucle pour remplir la durée cible.
 */
export const DEFAULT_MAX_CAPTURE_MS = 600_000; // 10 min

/**
 * Durée réellement capturée (ms) = min(durée cible, plafond). Borne basse à 0.
 * Ex. `captureWindowMs(60) → 60_000` ; `captureWindowMs(28800) → 600_000`.
 */
export function captureWindowMs(
  targetDurationSeconds: number,
  maxCaptureMs: number = DEFAULT_MAX_CAPTURE_MS,
): number {
  const target = Math.max(0, targetDurationSeconds) * 1000;
  return Math.min(target, Math.max(0, maxCaptureMs));
}

/** Bornes du multiplicateur de vitesse pour rester sain (anti-emballement). */
export const CAPTURE_SPEED_MIN = 0.5;
export const CAPTURE_SPEED_MAX = 600;

/**
 * Multiplicateur de vitesse à appliquer aux timers du player pour étirer (ou
 * comprimer) une playlist sur la fenêtre de capture. `baseTotalMs` = somme des
 * `estimateShowcaseDurationMs(questionCount_i)` de tous les exercices joués.
 *
 * `speedMult = clamp(captureMs / baseTotalMs, CAPTURE_SPEED_MIN, CAPTURE_SPEED_MAX)`.
 * Retourne 1 si `baseTotalMs <= 0` (rien à étirer).
 */
export function captureSpeedMult(captureMs: number, baseTotalMs: number): number {
  if (baseTotalMs <= 0) return 1;
  const raw = captureMs / baseTotalMs;
  return Math.min(CAPTURE_SPEED_MAX, Math.max(CAPTURE_SPEED_MIN, raw));
}

/**
 * Une entrée du planning des titres : un exercice de la playlist occupe la
 * fraction `[startFrac, endFrac]` du déroulé total (valeurs dans [0, 1]).
 * Indépendant du `speedMult` (c'est une part relative) → mappable sur n'importe
 * quelle durée réelle (la durée du webm capturé, côté worker/FFmpeg).
 */
export interface CaptureScheduleEntry {
  title: string;
  startFrac: number;
  endFrac: number;
}

/**
 * Découpe la playlist en fractions temporelles pondérées par la durée estimée
 * de chaque exercice (même pondération que la lecture réelle :
 * `estimateShowcaseDurationMs(questionCount)`). Utilisé pour afficher le bon
 * titre incrusté au bon moment dans la vidéo composée (overlay FFmpeg minuté).
 *
 * Pur & déterministe. Si tout est vide, renvoie une entrée plein-écran par exo.
 */
export function buildCaptureSchedule(
  items: { title: string; questionCount: number }[],
): CaptureScheduleEntry[] {
  const durs = items.map((it) => estimateShowcaseDurationMs(it.questionCount));
  const total = durs.reduce((a, b) => a + b, 0) || items.length || 1;
  let acc = 0;
  return items.map((it, i) => {
    const startFrac = acc / total;
    acc += durs[i] ?? 0;
    return { title: it.title, startFrac, endFrac: acc / total };
  });
}

/** Format court « 1 min 12 s » / « 45 s » à partir d'une durée en ms. */
export function formatDurationMs(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min === 0) return `${sec} s`;
  return sec === 0 ? `${min} min` : `${min} min ${sec} s`;
}
